import React from 'react';
import Grid from '@mui/material/Grid2';
import { Stack } from '@mui/material';
import { TypographyStepTitle, TypographyBold, TypographyInstruction, TypographyTitle } from '../style/typography';
import { RequestButton } from "../style/button";
import CommonCard from './common_card';
import BasicEarthquakeInfo from './basic_earthquake_info';
import CheckSummary from './check_summary';
import DamInspectionResult from './dam_Inspection_result';
import CheckResult from './check_result';
import CheckResultDam3 from './check_result_dam3';
import CreateExcelDialog from './create_excel_dialog';
import DownloadExcelDialog from './download_excel_dialog';
import RequestApprovalDialog from './request_approval_dialog';
import ReportApproval from './report_approval';
import PowerplantPatrol from './powerplant_patrol';

/**
 * HTMLコンテンツを安全にサニタイズする共通関数
 * @param {string} html - サニタイズするHTML文字列
 * @returns {string} サニタイズされたHTML
 */
export const sanitizeHTML = (html) => {
    return html ? html.replace(/<(?!br\s*\/?)[^>]+>/gi, '') : '';
};

/**
 * 基本的なステップレイアウトを提供するコンポーネント
 * @param {string} title - ステップのタイトル
 * @param {string|JSX.Element} lead - リード文（HTMLまたはJSXElement）
 * @param {Array<string>} descriptions - 詳細説明文の配列
 * @param {JSX.Element} children - メインコンテンツ
 * @param {boolean} useCard - CommonCardで囲むかどうか（デフォルト: false）
 */
export const BaseStepComponent = ({ title, lead, descriptions, children, useCard = false }) => {
    const renderLead = () => {
        if (!lead) return null;

        if (typeof lead === 'string') {
            if (lead.includes('<br')) {
                return <TypographyInstruction dangerouslySetInnerHTML={{ __html: sanitizeHTML(lead) }} />;
            } else {
                return <TypographyInstruction>{lead}<br /></TypographyInstruction>;
            }
        }

        return lead;
    };

    const mainContent = useCard ? <CommonCard child={children} /> : children;

    return (
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <TypographyStepTitle>{title}</TypographyStepTitle>
            {renderLead()}
            {descriptions && (
                <ul>
                    {descriptions.map((desc, index) => (
                        <li key={index}><TypographyInstruction>{desc}</TypographyInstruction></li>
                    ))}
                </ul>
            )}
            {mainContent}
        </Grid>
    );
};

/**
 * 基本情報確認ステップの共通コンポーネント
 */
export const BasicInfoStep = ({ title, lead, descriptions, earthquake, setEarthquake, isReadOnly }) => (
    <BaseStepComponent title={title} lead={lead} descriptions={descriptions} useCard={true}>
        <BasicEarthquakeInfo earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
    </BaseStepComponent>
);

/**
 * 点検概要ステップの共通コンポーネント
 */
export const CheckSummaryStep = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    checkResult,
    earthquake,
    isReadOnly
}) => (
    <BaseStepComponent title={title} lead={lead} descriptions={descriptions} useCard={true}>
        <CheckSummary
            checkSummary={checkSummary}
            setCheckSummary={setCheckSummary}
            isReadOnly={isReadOnly}
            checkResult={checkResult}
            earthquake={earthquake}
        />
    </BaseStepComponent>
);

/**
 * ダム本体点検結果ステップの共通コンポーネント
 */
export const DamInspectionResultStep = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    isReadOnly,
    type
}) => (
    <BaseStepComponent title={title} lead={lead} descriptions={descriptions} useCard={true}>
        <DamInspectionResult
            checkSummary={checkSummary}
            setCheckSummary={setCheckSummary}
            isReadOnly={isReadOnly}
            type={type}
        />
    </BaseStepComponent>
);

/**
 * チェック結果ステップの共通コンポーネント（第一報用）
 */
export const CheckResultStep = ({
    title,
    lead,
    descriptions,
    checkResult,
    setCheckResult,
    isReadOnly,
    facilityDams,
    useDam3 = false
}) => {
    const CheckComponent = useDam3 ? CheckResultDam3 : CheckResult;

    return (
        <BaseStepComponent title={title} lead={lead} descriptions={descriptions}>
            <CheckComponent
                checkResult={checkResult}
                setCheckResult={setCheckResult}
                isReadOnly={isReadOnly}
                facilityDams={facilityDams}
            />
        </BaseStepComponent>
    );
};

/**
 * 報告書作成・ダウンロードステップの共通コンポーネント
 */
export const ReportCreationStep = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    checkSummary,
    setCreateComplete,
    checkSummaryDataPrime,
    isCreateExcel,
    reportDir,
    facilityDams = [],
}) => (
    <BaseStepComponent title={title} lead={lead} descriptions={descriptions}>
        {isCreateExcel ? (
            <CreateExcelDialog
                earthquake={earthquake}
                checkResult={checkResult}
                checkSummary={checkSummary}
                setCreateComplete={setCreateComplete}
                checkSummaryDataPrime={checkSummaryDataPrime}
                facilityDams={facilityDams}
            />
        ) : (
            <DownloadExcelDialog
                earthquake={earthquake}
                report_dir={reportDir}
            />
        )}
    </BaseStepComponent>
);

/**
 * 承認依頼・承認実施ステップの共通コンポーネント
 */
export const ApprovalStep = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    checkSummary,
    createComplete,
    isApproveRequest,
    type,
    reportUuid,
    facilityDams = [],
}) => (
    <BaseStepComponent title={title} lead={lead} descriptions={descriptions}>
        {isApproveRequest ? (
            <RequestApprovalDialog
                earthquake={earthquake}
                checkResult={checkResult}
                checkSummary={checkSummary}
                type={type}
                createComplete={createComplete}
                facilityDams={facilityDams}
            />
        ) : (
            <ReportApproval
                report_or_inspection_uuid={reportUuid}
                earthquake_uuid={earthquake.uuid}
                type={type}
                report_uuid={earthquake.report_uuid}
            />
        )}
    </BaseStepComponent>
);

/**
 * 汎用的なタスクステップコンポーネント（task_step.jsxの改良版）
 * 箇条書きリストの有無で自動的にレイアウトを調整する
 */
export const TaskStep = ({
    title,
    instructions,
    listItems,
    appealContent,
    buttonText = '次へ',
    onNext,
    attachedFiles,
    startIcon
}) => {
    const styles = {
        stepAppealContent: { fontSize: '1rem' },
        stepInstruction: { whiteSpace: 'pre-line' },
        requestButtonContainer: { display: "flex", justifyContent: "center" }
    };

    const hasListItems = listItems && listItems.length > 0;

    const renderContent = () => (
        <>
            {appealContent && (
                <TypographyBold sx={styles.stepAppealContent}>{appealContent}</TypographyBold>
            )}
            {listItems && (
                <ul>
                    {listItems.map((item, index) => (
                        <li key={index}><TypographyInstruction>{item}</TypographyInstruction></li>
                    ))}
                </ul>
            )}
            {instructions && instructions.map((instruction, index) => (
                <TypographyInstruction
                    key={index}
                    sx={styles.stepInstruction}
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(instruction) }}
                />
            ))}
            {attachedFiles && attachedFiles.length > 0 && (
                <>
                    <TypographyBold sx={{ fontSize: '1rem' }}>
                        報告時の添付ファイル：
                    </TypographyBold>
                    <ul>
                        {attachedFiles.map((file, idx) => (
                            <li key={idx}>
                                <TypographyInstruction>{file}</TypographyInstruction>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </>
    );

    return (
        <>
            {hasListItems ? (
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <TypographyStepTitle>{title}</TypographyStepTitle>
                    <Stack spacing={{ xs: 1, sm: 2, md: 2 }} m={{ xs: 1, sm: 2, md: 2 }}>
                        {renderContent()}
                    </Stack>
                </Grid>
            ) : (
                <>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <TypographyStepTitle>{title}</TypographyStepTitle>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <Stack spacing={{ xs: 1, sm: 2, md: 2 }} m={{ xs: 1, sm: 2, md: 2 }}>
                            {renderContent()}
                        </Stack>
                    </Grid>
                </>
            )}
            <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={styles.requestButtonContainer}>
                <RequestButton onClick={onNext} startIcon={startIcon}>{buttonText}</RequestButton>
            </Grid>
        </>
    );
};

/**
 * 承認後のタスク表示コンポーネント（TaskStepの特化版）
 */
export const AfterApprovalTaskComponent = ({
    title,
    appealContent,
    lead,
    buttonContent = '次へ',
    onClickRequestButton,
    attachedFiles,
    listItems
}) => (
    <TaskStep
        title={title}
        instructions={[lead]}
        appealContent={appealContent}
        buttonText={buttonContent}
        onNext={onClickRequestButton}
        attachedFiles={attachedFiles}
        listItems={listItems}
    />
);

// ========== 第一報用のステップコンポーネント ==========

export const FirstReportStep01 = ({ descriptions, earthquake, setEarthquake, isReadOnly }) => (
    <BasicInfoStep
        title="STEP1：基本情報"
        lead="「地震発生日時」「最大震度」「震源地」を確認してください。"
        descriptions={descriptions}
        earthquake={earthquake}
        setEarthquake={setEarthquake}
        isReadOnly={isReadOnly}
    />
);

export const FirstReportStep02 = ({ title, lead, descriptions, checkResult, setCheckResult, isReadOnly, facilityDams }) => (
    <CheckResultStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkResult={checkResult}
        setCheckResult={setCheckResult}
        isReadOnly={isReadOnly}
        facilityDams={facilityDams}
        useDam3={true}
    />
);

export const FirstReportStep03 = ({ title, lead, descriptions, checkResult, setCheckResult, isReadOnly, facilityDams }) => (
    <CheckResultStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkResult={checkResult}
        setCheckResult={setCheckResult}
        isReadOnly={isReadOnly}
        facilityDams={facilityDams}
        useDam3={false}
    />
);

export const FirstReportStep04 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    checkSummary,
    setCreateComplete,
    isCreateExcel,
    facilityDams = [],
}) => (
    <ReportCreationStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkResult}
        checkSummary={checkSummary}
        setCreateComplete={setCreateComplete}
        isCreateExcel={isCreateExcel}
        facilityDams={facilityDams}
        reportDir="first_report"
    />
);

export const FirstReportStep05 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    createComplete,
    isApproveRequest,
    facilityDams = [],
}) => (
    <ApprovalStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkResult}
        createComplete={createComplete}
        isApproveRequest={isApproveRequest}
        type="initial"
        reportUuid={earthquake.initial_report_uuid}
        facilityDams={facilityDams}
    />
);

export const FirstReportAfterApprovalTask = (props) => (
    <AfterApprovalTaskComponent {...props} />
);

// ========== 第一段階点検用のステップコンポーネント ==========

export const PrimaryInspectionStep01 = ({ title, lead, descriptions, earthquake, setEarthquake, isReadOnly }) => (
    <BasicInfoStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        setEarthquake={setEarthquake}
        isReadOnly={isReadOnly}
    />
);

export const PrimaryInspectionStep02 = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    checkResult,
    earthquake,
    isReadOnly
}) => (
    <CheckSummaryStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkSummary={checkSummary}
        setCheckSummary={setCheckSummary}
        checkResult={checkResult}
        earthquake={earthquake}
        isReadOnly={isReadOnly}
    />
);

export const PrimaryInspectionStep03 = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    isReadOnly
}) => (
    <DamInspectionResultStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkSummary={checkSummary}
        setCheckSummary={setCheckSummary}
        isReadOnly={isReadOnly}
        type="primary"
    />
);

export const PrimaryInspectionStep04 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    checkSummary,
    setCreateComplete,
    checkSummaryDataPrime,
    isCreateExcel
}) => (
    <ReportCreationStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkResult}
        checkSummary={checkSummary}
        setCreateComplete={setCreateComplete}
        checkSummaryDataPrime={checkSummaryDataPrime}
        isCreateExcel={isCreateExcel}
        reportDir="primary_inspection_report"
    />
);

export const PrimaryInspectionStep05 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkSummary,
    createComplete,
    isApproveRequest
}) => (
    <ApprovalStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkSummary}
        checkSummary={checkSummary}
        createComplete={createComplete}
        isApproveRequest={isApproveRequest}
        type="primary"
        reportUuid={earthquake.primary_inspection_uuid}
    />
);

export const PrimaryInspectionAfterApprovalTask = (props) => (
    <AfterApprovalTaskComponent {...props} />
);

// ========== 第二段階点検用のステップコンポーネント ==========

export const SecondaryInspectionStep01 = ({ title, lead, descriptions, earthquake, setEarthquake, isReadOnly }) => (
    <BasicInfoStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        setEarthquake={setEarthquake}
        isReadOnly={isReadOnly}
    />
);

export const SecondaryInspectionStep02 = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    checkResult,
    earthquake,
    isReadOnly
}) => (
    <CheckSummaryStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkSummary={checkSummary}
        setCheckSummary={setCheckSummary}
        checkResult={checkResult}
        earthquake={earthquake}
        isReadOnly={isReadOnly}
    />
);

export const SecondaryInspectionStep03 = ({
    title,
    lead,
    descriptions,
    checkSummary,
    setCheckSummary,
    isReadOnly
}) => (
    <DamInspectionResultStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        checkSummary={checkSummary}
        setCheckSummary={setCheckSummary}
        isReadOnly={isReadOnly}
        type="secondary"
    />
);

export const SecondaryInspectionStep04 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkResult,
    checkSummary,
    setCreateComplete,
    checkSummaryDataPrime,
    isCreateExcel
}) => (
    <ReportCreationStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkResult}
        checkSummary={checkSummary}
        setCreateComplete={setCreateComplete}
        checkSummaryDataPrime={checkSummaryDataPrime}
        isCreateExcel={isCreateExcel}
        reportDir="secondary_inspection_report"
    />
);

export const SecondaryInspectionStep05 = ({
    title,
    lead,
    descriptions,
    earthquake,
    checkSummary,
    createComplete,
    isApproveRequest
}) => (
    <ApprovalStep
        title={title}
        lead={lead}
        descriptions={descriptions}
        earthquake={earthquake}
        checkResult={checkSummary}
        checkSummary={checkSummary}
        createComplete={createComplete}
        isApproveRequest={isApproveRequest}
        type="secondary"
        reportUuid={earthquake.secondary_inspection_uuid}
    />
);

export const SecondaryInspectionAfterApprovalTask = (props) => (
    <AfterApprovalTaskComponent {...props} />
);

export const CalloutStep = ({ step_title, lead, descriptions, children, useCard, title = '' }) => {
    // ボタンと他のコンテンツを分離
    const button = React.Children.toArray(children).find(
        child => React.isValidElement(child) && child.props.onCallout
    );
    
    const otherContent = React.Children.toArray(children).filter(
        child => !React.isValidElement(child) || !child.props.onCallout
    );

    return (
        <>
            {title && (
                <Grid size={{ xs: 12 }}>
                    <TypographyTitle>{title}</TypographyTitle>
                </Grid>
            )}
            <Grid
                container
                spacing={2}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                }}
            >
                <Grid size={{ xs: 12, md: 9 }}>
                    <BaseStepComponent
                        title={step_title}
                        lead={lead}
                        descriptions={descriptions}
                        useCard={useCard}
                    />
                </Grid>
                {button && (
                    <Grid 
                        size={{ xs: 12, md: 3 }} 
                        sx={{ 
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            ml: 'auto'
                        }}
                    >
                        {button}
                    </Grid>
                )}
            </Grid>
            {otherContent.length > 0 && (
                <Grid
                    size={{ xs: 12 }}
                    sx={{ m: 0 }}
                >
                    {otherContent}
                </Grid>
            )}
        </>
    );
};

// ========== 発電所巡視要請用のステップコンポーネント ==========
export const PowerplantPatrolStep01 = ({ title }) => (
    <PowerplantPatrol
        title={title}
    />
);

export const PowerplantStep = ({title, lead}) => {
    <>
        <BaseStepComponent
            title={title}
            lead={lead}
        />
    </>
}